import type { Rail } from "../level";
import { coping, type QuarterPrim } from "./quarter";
import { rad, type PieceKind } from "./types";

export interface SpineSpec {
  /** Centre of the coping. */
  x: number;
  z: number;
  /** The two sides rise toward the coping from ∓(cos rot, sin rot). */
  rot: number;
  /** Transition radius (and height) of both sides. */
  r: number;
  /** Half the length of the coping. */
  hw: number;
}

/** Two quarters back to back sharing one coping (see `spine`). */
export interface SpinePiece {
  kind: "spine";
  x: number;
  z: number;
  rot?: number;
  r: number;
  hw: number;
}

/**
 * Two quarter pipes back to back with no deck, sharing one coping. Launch
 * vert off either side and you come back down it; R2 in the air transfers
 * over the top (`Skater` spots the spine from the surface past the coping).
 */
export function spine(spec: SpineSpec): { prims: QuarterPrim[]; rails: Rail[] } {
  const c = Math.cos(spec.rot);
  const s = Math.sin(spec.rot);
  const side = (sign: 1 | -1): QuarterPrim => ({
    kind: "quarter",
    x: spec.x - sign * spec.r * c,
    z: spec.z - sign * spec.r * s,
    rot: sign === 1 ? spec.rot : spec.rot + Math.PI,
    r: spec.r,
    hw: spec.hw,
    deck: 0,
  });
  const sides = [side(1), side(-1)];
  return { prims: sides, rails: [coping(sides[0]!)] };
}

export const SPINE_PIECE: PieceKind<SpinePiece> = {
  fields: ["x", "z", "rot", "r", "hw"],
  placed: true,
  check(c, o, path) {
    c.num(o, "r", path, { min: 0.5, max: 20 });
  },
  compile(p, out) {
    const sp = spine({ x: p.x, z: p.z, rot: rad(p.rot), r: p.r, hw: p.hw });
    out.prims.push(...sp.prims);
    out.rails.push(...sp.rails);
  },
};
